import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useReducedMotion } from 'framer-motion';

interface TypewriterProps {
  lines: string[];
  typeSpeed?: number;
  deleteSpeed?: number;
  holdTime?: number;
}

/**
 * Cycles through `lines`, typing then deleting each — a subtle terminal hint.
 * Under reduced-motion it simply shows the first line (no animation).
 */
const Typewriter: React.FC<TypewriterProps> = ({
  lines,
  typeSpeed = 55,
  deleteSpeed = 30,
  holdTime = 1600,
}) => {
  const prefersReduced = useReducedMotion();
  const [text, setText] = useState('');
  const [lineIndex, setLineIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (prefersReduced) return;
    const current = lines[lineIndex % lines.length];

    let delay: number;
    if (!deleting && text === current) {
      delay = holdTime;
    } else if (deleting && text === '') {
      delay = 350;
    } else {
      delay = deleting ? deleteSpeed : typeSpeed;
    }

    const timer = setTimeout(() => {
      if (!deleting && text === current) {
        setDeleting(true);
      } else if (deleting && text === '') {
        setDeleting(false);
        setLineIndex((i) => (i + 1) % lines.length);
      } else {
        const next = deleting
          ? current.slice(0, text.length - 1)
          : current.slice(0, text.length + 1);
        setText(next);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [text, deleting, lineIndex, lines, typeSpeed, deleteSpeed, holdTime, prefersReduced]);

  return (
    <Box
      component="span"
      sx={{
        fontFamily: (t) => t.typography.button.fontFamily,
        color: 'text.secondary',
      }}
    >
      <Box component="span" sx={{ color: 'primary.main', mr: 1 }}>
        &gt;
      </Box>
      {prefersReduced ? lines[0] : text}
      {!prefersReduced && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: '0.6em',
            ml: '2px',
            color: 'primary.main',
            animation: 'tw-blink 1s steps(1) infinite',
            '@keyframes tw-blink': { '50%': { opacity: 0 } },
          }}
        >
          ▌
        </Box>
      )}
    </Box>
  );
};

export default Typewriter;
